import downloadIcon from '@disy/cadenza-icons/export.svg';

import { alert } from 'cadenza/alert';
import { MIMETYPE_XLSX, MIMETYPE_KML } from 'cadenza/api-client/api-constants';
import { canExport, startExport } from 'cadenza/api-client/layer-export-api';
import { LayerProperty } from 'cadenza/map/layer-property';
import { icon } from 'cadenza/utils/icon/icon';
import { BackendAbortController } from 'cadenza/api-client/abort-controller/backend-abort-controller';
import { FileExportProgressModal } from 'cadenza/file-export-progress-dialog/file-export-progress-modal';

import { LayerAction } from './layer-action';
import i18n from './layer-actions.properties';
import { confirmExport } from './export-layer-confirm-dialog';

export class ExportAction extends LayerAction {

  #title;
  #map;
  #abortController;
  #mimeType;
  #isSupported;

  constructor (layer, map, { title, mimeType, isSupported, ...options }) {
    super(layer, Object.assign({
      icon: icon(downloadIcon)
    }, options));
    this.#title = title;
    this.#map = map;
    this.#mimeType = mimeType;
    this.#isSupported = isSupported === undefined ? true : isSupported;

    this.on('cancelled', () => this._onActionCancelled());
  }

  isCancellable () {
    return true;
  }

  execute () {
    if (this.#abortController) {
      this.#abortController.abort();
    }
    this.#abortController = new BackendAbortController();
    this.#abortController.abortOnUnload = true;
    const layerId = this._layer.get(LayerProperty.ID);

    // ideally the canExport should run when the dialog is already open so that the user has a chance to actually cancel it
    return canExport(
      layerId,
      this.#map.mapScale,
      this.#mimeType,
      this.#abortController.signal
    )
      .then(result => {
        if (result.type === ConditionStatus.ERROR) {
          alert(
            i18n('layerExport.errorDialog.title'),
            i18n(
              'layerExport.errorDialog.rowCount.error',
              {
                actualRows: result.actualRows,
                maximumRows: result.maximumRows
              }),
            { type: 'info' }
          );
          throw new Error('Cannot export data, too many rows.');
        } else {
          return confirmExport({
            title: i18n(this.#title),
            layer: this._layer,
            mimeType: this.#mimeType,
            warning: result.type === ConditionStatus.WARNING,
            rowCount: result.actualRows
          });
        }
      })
      .then((includeCoordinates = false) => {
        this.#abortController = new BackendAbortController();
        this.#abortController.abortOnUnload = true;

        const dialog = new FileExportProgressModal(
          this._getTitleForDialog(),
          () => startExport(
            layerId,
            this.#map.mapScale,
            this.#map.getProjection().getCode(),
            includeCoordinates,
            this.#mimeType)
        );

        dialog.startExportAndShowProgress();
      })
      .catch((error) => {
        // no need to log as the user was informed in a dialog
      });
  }

  isSupportedSync ({ isEditingLayer }) {
    return !isEditingLayer && this._layer.get(LayerProperty.HAS_FEATURES) && this.#isSupported;
  }

  hasIcon (forShortcutAction) {
    return !forShortcutAction; // We have, but not for shortcutActions
  }

  _onActionCancelled () {
    if (this.#abortController) {
      this.#abortController.abort();
    }
  }

  _getTitleForDialog () {
    if (this.#mimeType === MIMETYPE_KML) {
      return i18n('layerExport.fileExportProgressDialog.title.kml');
    }
    if (this.#mimeType === MIMETYPE_XLSX) {
      return i18n('layerExport.fileExportProgressDialog.title.xlsx');
    }
    return i18n('layerExport.fileExportProgressDialog.title.default');
  }

}

/**
 * @enum {string}
 * @readonly
 * @see net/disy/gis/services/layer/export/api/rest/base/ExportPermissionResult
 */
export const ConditionStatus = {
  ERROR: 'ERROR',
  WARNING: 'WARNING'
};
